// TODO: Review AI Code for feature: Process Map - Extracted from ProcessMapPage.tsx
import { useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Map,
  Cog,
  Heart,
  Shield,
  Unplug,
  Link2,
  Search,
  X,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import {
  StatCard,
  Button,
  Badge,
  ConfirmDialog,
  Input,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@bliks-io/ui';
import type { useProcessMap } from '@/api/queries/useProcessMap';
import type { MapData } from './components/processMapContexts';
import { FilterCombobox } from './FilterCombobox';

// ============================================================================
// KPI Cards
// ============================================================================

export function KpiCards({ summary }: { summary: MapData['summary'] }) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
      <StatCard
        icon={<Map className="w-5 h-5 text-info" />}
        title={t('processMap.kpi.total')}
        value={summary.totalProcesses}
        description=""
      />
      <StatCard
        icon={<Heart className="w-5 h-5 text-positive" />}
        title={t('processMap.kpi.core')}
        value={summary.coreProcesses}
        description=""
      />
      <StatCard
        icon={<Shield className="w-5 h-5 text-highlight" />}
        title={t('processMap.kpi.management')}
        value={summary.managementProcesses}
        description=""
      />
      <StatCard
        icon={<Cog className="w-5 h-5 text-muted-foreground" />}
        title={t('processMap.kpi.support')}
        value={summary.supportProcesses}
        description=""
      />
      <StatCard
        icon={<Link2 className="w-5 h-5 text-info" />}
        title={t('processMap.kpi.connections')}
        value={summary.totalConnections}
        description=""
      />
      <StatCard
        icon={<Unplug className="w-5 h-5 text-warning" />}
        title={t('processMap.kpi.isolated')}
        value={summary.isolatedProcesses}
        description=""
        className={summary.isolatedProcesses > 0 ? 'border-warning-line' : undefined}
      />
    </div>
  );
}

// ============================================================================
// Department Filter Control
// ============================================================================

export function DepartmentFilterControl({
  sortedDepartments,
  filteredDepartments,
  selectedDepartments,
  selectedDeptTriggerLabel,
  handleDepartmentChange,
  deptSearch,
  setDeptSearch,
  departmentFilterMode,
  toggleDepartmentFilterMode
}: {
  sortedDepartments: {
    department: { _id: string; name: string; abbreviation?: string };
    prefix: string;
  }[];
  filteredDepartments: {
    department: { _id: string; name: string; abbreviation?: string };
    prefix: string;
  }[];
  selectedDepartments: string[];
  selectedDeptTriggerLabel: React.ReactNode;
  handleDepartmentChange: (value: string | string[] | undefined) => void;
  deptSearch: string;
  setDeptSearch: (s: string) => void;
  departmentFilterMode: 'highlight' | 'filter';
  toggleDepartmentFilterMode: () => void;
}) {
  const { t } = useTranslation();
  if (sortedDepartments.length === 0) return null;
  return (
    <FilterCombobox
      items={filteredDepartments}
      selectedValues={selectedDepartments}
      onValueChange={handleDepartmentChange}
      getItemValue={(item) => item.department._id}
      renderItem={(item) => (
        <span className="flex items-center flex-1 gap-2">
          {item.prefix && (
            <span className="font-mono whitespace-pre text-muted-foreground/50">{item.prefix}</span>
          )}
          <span className="truncate">{item.department.name}</span>
          {item.department.abbreviation && (
            <Badge variant="secondary" className="ml-auto text-white shrink-0">
              {item.department.abbreviation}
            </Badge>
          )}
        </span>
      )}
      triggerLabel={selectedDeptTriggerLabel}
      placeholder={t('processMap.departmentFilter.placeholder')}
      searchPlaceholder={t('processMap.departmentFilter.search')}
      emptyMessage={t('processMap.departmentFilter.empty')}
      filterMode={departmentFilterMode}
      onToggleFilterMode={toggleDepartmentFilterMode}
      highlightModeTitle={t('processMap.departmentFilter.modeHighlight')}
      filterModeTitle={t('processMap.departmentFilter.modeFilter')}
      contentClassName="w-[400px]"
      search={deptSearch}
      onSearchChange={setDeptSearch}
    />
  );
}

// ============================================================================
// Chain Filter Control
// ============================================================================

export function ChainFilterControl({
  chainsWithProcesses,
  selectedChains,
  selectedChainTriggerLabel,
  handleChainChange,
  chainFilterMode,
  toggleChainFilterMode
}: {
  chainsWithProcesses: { _id: string; name: string; processCount: number }[];
  selectedChains: string[];
  selectedChainTriggerLabel: React.ReactNode;
  handleChainChange: (value: string | string[] | undefined) => void;
  chainFilterMode: 'highlight' | 'filter';
  toggleChainFilterMode: () => void;
}) {
  const { t } = useTranslation();
  if (chainsWithProcesses.length === 0) return null;
  return (
    <FilterCombobox
      items={chainsWithProcesses}
      selectedValues={selectedChains}
      onValueChange={handleChainChange}
      getItemValue={(chain) => chain._id}
      renderItem={(chain) => (
        <>
          <span className="flex-1 truncate">{chain.name}</span>
          <Badge variant="secondary" className="ml-auto shrink-0 text-3xs">
            {chain.processCount}
          </Badge>
        </>
      )}
      triggerLabel={selectedChainTriggerLabel}
      placeholder={t('processMap.chainFilter.placeholder')}
      searchPlaceholder={t('processMap.chainFilter.search')}
      emptyMessage={t('processMap.chainFilter.empty')}
      filterMode={chainFilterMode}
      onToggleFilterMode={toggleChainFilterMode}
      highlightModeTitle={t('processMap.chainFilter.modeHighlight')}
      filterModeTitle={t('processMap.chainFilter.modeFilter')}
    />
  );
}

// ============================================================================
// Search Control
// ============================================================================

export function ProcessMapSearchControl({
  searchQuery,
  setSearchQuery,
  handleSearchKeyDown,
  searchMatchIds,
  focusedSearchIndex,
  goToPrevMatch,
  goToNextMatch
}: {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  handleSearchKeyDown: (e: React.KeyboardEvent) => void;
  searchMatchIds: string[];
  focusedSearchIndex: number;
  goToPrevMatch: () => void;
  goToNextMatch: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          placeholder={t('processMap.search.placeholder')}
          className="h-8 text-xs! pl-7 pr-7 w-[160px] py-1"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute -translate-y-1/2 right-2 top-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {searchMatchIds.length > 0 && (
        <div className="flex items-center gap-0.5">
          <Badge
            variant="secondary"
            className="shrink-0 text-3xs px-1.5 py-0 h-5 bg-warning-surface-strong text-warning-strong border-warning-line tabular-nums"
          >
            {focusedSearchIndex >= 0
              ? `${focusedSearchIndex + 1} / ${searchMatchIds.length}`
              : searchMatchIds.length}
          </Badge>
          <Button variant="ghost" size="small" className="w-6 h-6 p-0" onClick={goToPrevMatch}>
            <ChevronUp className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="small" className="w-6 h-6 p-0" onClick={goToNextMatch}>
            <ChevronDown className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
      {searchQuery.trim() && searchMatchIds.length === 0 && (
        <Badge
          variant="secondary"
          className="shrink-0 text-3xs px-1.5 py-0 h-5 bg-danger-surface text-danger border-danger-line"
        >
          {t('processMap.search.noResults')}
        </Badge>
      )}
    </div>
  );
}

// ============================================================================
// Confirmation Dialogs
// ============================================================================

export function ProcessMapConfirmDialogs({
  showResetConfirm,
  setShowResetConfirm,
  handleResetConfirm,
  resetPending,
  showDiscardConfirm,
  setShowDiscardConfirm,
  handleDiscardConfirm
}: {
  showResetConfirm: boolean;
  setShowResetConfirm: (open: boolean) => void;
  handleResetConfirm: () => void;
  resetPending: boolean;
  showDiscardConfirm: boolean;
  setShowDiscardConfirm: (open: boolean) => void;
  handleDiscardConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <ConfirmDialog
        open={showResetConfirm}
        onOpenChange={setShowResetConfirm}
        variant="danger"
        title={t('processMap.resetLayout')}
        description={t('processMap.resetLayoutConfirm')}
        confirmLabel={t('processMap.resetLayout')}
        onConfirm={handleResetConfirm}
        isPending={resetPending}
      />

      <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.unsavedChanges')}</DialogTitle>
            <DialogDescription>{t('common.unsavedChangesDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscardConfirm(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDiscardConfirm}>
              {t('processMap.discardChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// Search Hook
// ============================================================================

export function useProcessMapSearch(
  data: ReturnType<typeof useProcessMap>['data'],
  focusProcessId: string | undefined
) {
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedSearchIndex, setFocusedSearchIndex] = useState(-1);
  const prevMatchCountRef = useRef(0);

  const searchMatchIds = useMemo(() => {
    if (!searchQuery.trim() || !data) return [];
    const q = searchQuery.toLowerCase();
    return data.processes.filter((p) => p.name.toLowerCase().includes(q)).map((p) => p._id);
  }, [searchQuery, data]);

  // Reset index when match list changes
  if (searchMatchIds.length !== prevMatchCountRef.current) {
    prevMatchCountRef.current = searchMatchIds.length;
    if (
      searchMatchIds.length > 0 &&
      (focusedSearchIndex < 0 || focusedSearchIndex >= searchMatchIds.length)
    ) {
      setFocusedSearchIndex(0);
    } else if (searchMatchIds.length === 0) {
      setFocusedSearchIndex(-1);
    }
  }

  const focusNodeId =
    focusedSearchIndex >= 0 && focusedSearchIndex < searchMatchIds.length
      ? searchMatchIds[focusedSearchIndex]
      : (focusProcessId ?? undefined);

  const goToNextMatch = useCallback(() => {
    if (searchMatchIds.length === 0) return;
    setFocusedSearchIndex((i) => (i + 1) % searchMatchIds.length);
  }, [searchMatchIds]);

  const goToPrevMatch = useCallback(() => {
    if (searchMatchIds.length === 0) return;
    setFocusedSearchIndex((i) => (i - 1 + searchMatchIds.length) % searchMatchIds.length);
  }, [searchMatchIds]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        goToNextMatch();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        goToPrevMatch();
      }
    },
    [goToNextMatch, goToPrevMatch]
  );

  return {
    searchQuery,
    setSearchQuery,
    searchMatchIds,
    focusedSearchIndex,
    focusNodeId,
    goToNextMatch,
    goToPrevMatch,
    handleSearchKeyDown
  };
}
